/**
 * Keeps Shopify Inbox (shopify-chat) at the theme offset after expand/collapse.
 * Inbox copies --shopify-agent-activator-offset onto nested shadow hosts, which
 * drops the theme CSS. Adopted sheets inside open shadows restore it.
 */
const OFFSET_SHEET = new CSSStyleSheet();
OFFSET_SHEET.replaceSync(`
:host {
  --shopify-agent-activator-offset: var(--bayse-shopify-chat-offset) !important;
}
.activator-nudge {
  bottom: calc(var(--shopify-agent-activator-offset) + var(--private-preview-bar-offset, 0px)) !important;
}
`);

const boundHosts = new WeakSet();

/**
 * Adopts the offset stylesheet into an open shadow root.
 * @param {ShadowRoot | null} root
 */
const injectOffsetSheet = (root) => {
  if (!root) return;
  if (root.adoptedStyleSheets.includes(OFFSET_SHEET)) return;
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, OFFSET_SHEET];
};

/**
 * Binds a Shopify chat host (and nested agent runtime) so collapse keeps theme position.
 * INP risk: low — observer only injects a stylesheet and walks a few custom elements.
 * @param {Element} host
 */
const bindChatHost = (host) => {
  if (!(host instanceof HTMLElement) || boundHosts.has(host)) return;
  boundHosts.add(host);

  const sync = () => {
    injectOffsetSheet(host.shadowRoot);
    const nested = host.shadowRoot?.querySelectorAll('shopify-agent-runtime, shopify-chat');
    nested?.forEach((node) => bindChatHost(node));
  };

  sync();

  const observer = new MutationObserver(sync);
  observer.observe(host, { attributes: true, attributeFilter: ['view', 'open', 'style'] });
  if (host.shadowRoot) {
    observer.observe(host.shadowRoot, { childList: true, subtree: true });
  }
};

/**
 * Finds shopify-chat once it is injected and binds positioning.
 */
const watchForChat = () => {
  const existing = document.querySelector('shopify-chat');
  if (existing) {
    bindChatHost(existing);
    return;
  }

  const wait = new MutationObserver(() => {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    bindChatHost(chat);
    wait.disconnect();
  });
  wait.observe(document.documentElement, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchForChat, { once: true });
} else {
  watchForChat();
}
